import { FunctionComponent, JSX, useEffect, useMemo, useState } from 'react'

import { importDependency } from 'core-components/helpers'
import { Loader } from 'core-components/library/loader'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import {
  ISiteBuilderContext,
  SiteBuilderContext,
} from 'core-components/sitebuilder/layout/context/SiteBuilderContext'
import { IAppLazyWidget, TAppWidget } from 'core-components/sitebuilder/models/IWidgetSelector'
import { TAppIframeWidgetData } from 'src/core/classes/models/Widget'
import { AppLoader } from 'src/core/components/app/AppLoader'
import { CoreWidgets } from 'src/core/sitebuilder/layout/CoreWidgets'
import { useFlexGridWrapperContextValues } from 'src/core/sitebuilder/layout/hooks/useFlexGridWrapperContextValues'

interface ISiteBuilderWrapper {
  children: JSX.Element
  AppIframeWidgetData?: TAppIframeWidgetData
  AppWidgets: TAppWidget
  AppLazyWidgets?: IAppLazyWidget[]
}

export const SiteBuilderWrapper = ({
  children,
  AppWidgets,
  AppLazyWidgets = [],
  AppIframeWidgetData,
}: ISiteBuilderWrapper) => {
  const [loading, setLoading] = useState<boolean>(!!AppLazyWidgets?.length)
  const { flexGridWrapperContextValues } = useFlexGridWrapperContextValues()
  const [lazyWidgets, setLazyWidgets] = useState<{ [key: string]: TAppWidget }>({})

  const appContextValue = useMemo<ISiteBuilderContext>(() => {
    return {
      AppWidgets: (widgetSelectorProps) => {
        return (
          <CoreWidgets {...widgetSelectorProps} AppWidgets={AppWidgets} lazyWidgets={lazyWidgets} />
        )
      },
      getAppIframeWidgets: AppIframeWidgetData,
      settingsLayout: {
        selectorData: {
          sizeSelector: [0, 4, 8, 12, 16, 20].map((el) => ({ id: `${el}`, label: `${el}px` })),
        },
      },
    }
  }, [AppIframeWidgetData, AppWidgets, lazyWidgets])

  useEffect(() => {
    if (AppLazyWidgets?.length) {
      setLoading(true)
      const promises = []
      for (const lazyWidget of AppLazyWidgets) {
        promises.push(importDependency(lazyWidget.key, lazyWidget.loader))
      }
      Promise.allSettled(promises)
        .then((data) => {
          const keys = Object.keys(AppLazyWidgets)
          const result: { [key: string]: TAppWidget } = {}
          for (let i = 0; i < keys.length; i++) {
            if (data[i].status === 'fulfilled') {
              result[AppLazyWidgets[i].key] = (
                data[i] as PromiseFulfilledResult<FunctionComponent>
              ).value
            }
          }
          setLazyWidgets(result)
        })
        .finally(() => setLoading(false))
    }
  }, [AppLazyWidgets])

  if (loading) {
    return <AppLoader />
  }

  return (
    <FlexGridWrapperContext.Provider value={flexGridWrapperContextValues}>
      <SiteBuilderContext.Provider value={appContextValue}>{children}</SiteBuilderContext.Provider>
    </FlexGridWrapperContext.Provider>
  )
}
